import { create } from 'zustand';
import type { ModalComponent, ModalConfig } from '../interfaces/modal-config';
import { ModalType } from '../interfaces/modal-types.enum';

// Port of src/app/services/modal.service.ts.
// The ModalType → component map is registered by ModalHost at startup (the
// Angular service imported the 12 modal components directly; in React that
// would create an import cycle with components that call openModal).
const modalComponents = new Map<ModalType, ModalComponent>();

export function registerModalComponents(entries: Array<[ModalType, ModalComponent]>) {
  for (const [type, component] of entries) {
    modalComponents.set(type, component);
  }
}

interface ModalState {
  activeModals: ModalConfig[];
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  open: (modalType: ModalType, config?: Partial<ModalConfig>) => string | undefined;
  close: (modalId: string) => void;
  closeAll: () => void;
}

export const useModalStore = create<ModalState>()((set, get) => ({
  activeModals: [],
  isLoading: false,

  setLoading: (loading) => set({ isLoading: loading }),

  open: (modalType, config = {}) => {
    const component = modalComponents.get(modalType);
    if (!component) {
      throw new Error(`Modal component not found for type: ${modalType}`);
    }

    const isModalAlreadyOpen = get().activeModals.some(modal => modal.component === component);
    if (isModalAlreadyOpen) {
      return undefined;
    }

    const modalId = 'modal-' + Math.random().toString(36).substring(2, 11);
    const modalConfig: ModalConfig = {
      id: modalId,
      component,
      inputs: {
        ...config.inputs,
        modalId
      },
      zIndex: config.zIndex
    };

    set(state => ({ activeModals: [...state.activeModals, modalConfig] }));
    return modalId;
  },

  close: (modalId) => set(state => ({
    activeModals: state.activeModals.filter(modal => modal.id !== modalId)
  })),

  closeAll: () => set({ activeModals: [] }),
}));
